import { isArray } from 'lodash';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { styles as sharedStyles } from '@swf/account';
import ResolverLink from '@swf/components/ResolverLink/ResolverLink';
import StarsLink from '@swf/components/StarsLink/StarsLink';
import Translation from '@swf/components/Translation/Translation';
import { call } from '@swf/core/api/embedded';
import MinIcon from '@swf/core/assets/icons/minus-icon.svg';
import ExpandIcon from '@swf/core/assets/icons/plus-icon.svg';
import { FeatureFlag, ResolverTag } from '@swf/core/enums';
import useFeature from '@swf/core/hooks/useFeature';
import { select } from '@swf/core/store';
import type { Dispatch } from '@swf/core/store';
import { License } from '@swf/core/types/Account';
import type { TranslationTag } from '@swf/core/types/i18n';

import FormContentDESH from '../../shared/TakeABreakFormContentDESH/FormContentDESH';
import styles from '../TakeABreakFromUs.scss';

import {
  TXTCLI_YesX,
  TXTWCL_Alternatively_you_can_choose_t_ELL,
  TXTWCL_Can_I_still_access_my_accountX,
  TXTWCL_Can_I_still_access_my_account__ELL,
  TXTWCL_Can_I_still_access_my_account__ELL_1,
  TXTWCL_Can_I_take_a_break_from_all_ga_ELL,
  TXTWCL_Can_this_be_undoneX,
  TXTWCL_Can_you_exclude_me_permanently_ELL,
  TXTWCL_DonXt_worryX_weXll_automatical_ELL,
  TXTWCL_For_anything_over_N0_daysX_you_ELL,
  TXTWCL_How_can_I_manage_my_spendingX,
  TXTWCL_How_long_can_I_be_excluded_for_ELL,
  TXTWCL_How_long_can_I_take_a_break_fo_ELL,
  TXTWCL_HoweverX_you_wonXt_be_able_to__ELL,
  TXTWCL_If_youXre_not_sure_about_takin_ELL,
  TXTWCL_If_youXre_not_sure_about_takin_ELL_2,
  TXTWCL_In_case_you_are_interestedX_we_ELL,
  TXTWCL_ItXs_basically_a_pause_on_your_ELL,
  TXTWCL_ItXs_basically_a_pause_on_your_ELL_1,
  TXTWCL_Just_contact_us_through_the_Xa_ELL,
  TXTWCL_Known_as_XbXself_exclusionX_bX_ELL,
  TXTWCL_Learn_more_about_XaXResponsibl_ELL,
  TXTWCL_MoreoverX_you_will_be_automati_ELL,
  TXTWCL_NoX_2,
  TXTWCL_No_you_cannotX,
  TXTWCL_Of_courseX_1,
  TXTWCL_Of_courseX_Just_XaXcontact_usX_ELL,
  TXTWCL_Once_youXve_decided_to_take_a__ELL,
  TXTWCL_Our_customer_services_team_wil_ELL,
  TXTWCL_Simply_visit_XaXwwwXgamstopXco_ELL,
  TXTWCL_This_can_be_anywhere_from_1_da_ELL,
  TXTWCL_This_can_be_anywhere_from_N0_d_ELL,
  TXTWCL_This_can_be_anywhere_from_N0_d_ELL_1,
  TXTWCL_This_can_be_anywhere_from_N0_h_ELL,
  TXTWCL_This_can_be_anywhere_from_N0_h_ELL_1,
  TXTWCL_This_can_be_anywhere_from_N0_h_ELL_2,
  TXTWCL_To_find_out_more_about_safe_ga_ELL,
  TXTWCL_UnfortunatelyX_we_canXt_extend_ELL,
  TXTWCL_Unfortunately_notX_Once_youXve_ELL,
  TXTWCL_Unfortunately_we_canXt_extend__ELL,
  TXTWCL_We_also_wonXt_send_you_any_new_ELL,
  TXTWCL_What_about_bonusesX,
  TXTWCL_What_does_this_meanX,
  TXTWCL_What_if_IXve_signed_up_to_tour_ELL,
  TXTWCL_When_your_break_endsX_you_will_ELL,
  TXTWCL_Where_can_I_ask_for_advice_on__ELL,
  TXTWCL_XbXWhat_happens_when_my_break__ELL,
  TXTWCL_YesX_for_anything_less_than_N0_ELL,
  TXTWCL_Yes_you_canX_But_you_wonXt_be__ELL,
  TXTWCL_Yes_you_canX_HoweverX_you_wonX_ELL,
  TXTWCL_You_can_also_set_a_XaXloss_lim_ELL,
  TXTWCL_You_can_choose_between_3_month_ELL,
  TXTWCL_You_choose_the_start_and_end_d_ELL_1,
  TXTWCL_You_wonXt_receive_any_newslett_ELL
} from './TakeABreakFromUsInformation.messages.json';

interface QuestionAnswerTagsPair {
  infoQuestion: TranslationTag;
  infoAnswer: TranslationTag;
}

const licenseSpecificPeriod = {
  [License.USMI]: [3, 5],
  [License.USPA]: [3, 1],
  [License.UK]: [12, 5],
  [License.SE]: [24, 1],
  [License.DESH]: [24, 180],
  [License.CAON]: [24, 90],
  default: [12, 180]
};

const licenseSpecificText = {
  [License.USMI]: TXTWCL_This_can_be_anywhere_from_N0_d_ELL,
  [License.USPA]: TXTWCL_This_can_be_anywhere_from_N0_d_ELL_1,
  [License.SE]: TXTWCL_This_can_be_anywhere_from_N0_h_ELL_1,
  [License.UK]: TXTWCL_This_can_be_anywhere_from_N0_h_ELL_2,
  default: TXTWCL_This_can_be_anywhere_from_N0_h_ELL
};

const infoMessages = {
  selfExclusionDE: [
    {
      infoQuestion: TXTWCL_How_long_can_I_take_a_break_fo_ELL,
      infoAnswer: TXTWCL_You_can_choose_between_3_month_ELL
    },
    {
      infoQuestion: TXTWCL_XbXWhat_happens_when_my_break__ELL,
      infoAnswer: TXTWCL_When_your_break_endsX_you_will_ELL
    },
    {
      infoQuestion: TXTWCL_Can_this_be_undoneX,
      infoAnswer: [
        TXTWCL_NoX_2,
        TXTWCL_Once_youXve_decided_to_take_a__ELL,
        TXTWCL_Our_customer_services_team_wil_ELL
      ]
    },
    {
      infoQuestion: TXTWCL_Can_I_still_access_my_accountX,
      infoAnswer: [
        TXTWCL_Yes_you_canX_HoweverX_you_wonX_ELL,
        TXTWCL_You_wonXt_receive_any_newslett_ELL
      ]
    },
    {
      infoQuestion: TXTWCL_What_about_bonusesX,
      infoAnswer: TXTWCL_UnfortunatelyX_we_canXt_extend_ELL
    },
    {
      infoQuestion: TXTWCL_What_if_IXve_signed_up_to_tour_ELL,
      infoAnswer: TXTWCL_DonXt_worryX_weXll_automatical_ELL
    }
  ],
  [License.CAON]: [
    {
      infoQuestion: TXTWCL_Can_this_be_undoneX,
      infoAnswer: TXTWCL_Unfortunately_notX_Once_youXve_ELL
    },
    {
      infoQuestion: TXTWCL_Can_I_still_access_my_account__ELL,
      infoAnswer: TXTWCL_Yes_you_canX_But_you_wonXt_be__ELL
    },
    {
      infoQuestion: TXTWCL_Can_I_still_access_my_account__ELL_1,
      infoAnswer: [TXTWCL_No_you_cannotX, TXTWCL_MoreoverX_you_will_be_automati_ELL]
    },
    {
      infoQuestion: TXTWCL_What_about_bonusesX,
      infoAnswer: TXTWCL_Unfortunately_we_canXt_extend__ELL
    },
    {
      infoQuestion: TXTWCL_What_if_IXve_signed_up_to_tour_ELL,
      infoAnswer: TXTWCL_DonXt_worryX_weXll_automatical_ELL
    },
    {
      infoQuestion: TXTWCL_How_can_I_manage_my_spendingX,
      infoAnswer: TXTWCL_If_youXre_not_sure_about_takin_ELL
    }
  ],
  selfExclusionUK: [
    {
      infoQuestion: TXTWCL_Can_this_be_undoneX,
      infoAnswer: TXTWCL_Unfortunately_notX_Once_youXve_ELL
    },
    {
      infoQuestion: TXTWCL_Can_I_still_access_my_accountX,
      infoAnswer: [
        TXTWCL_YesX_for_anything_less_than_N0_ELL,
        TXTWCL_HoweverX_you_wonXt_be_able_to__ELL,
        TXTWCL_We_also_wonXt_send_you_any_new_ELL,
        TXTWCL_For_anything_over_N0_daysX_you_ELL
      ]
    },
    {
      infoQuestion: TXTWCL_What_about_bonusesX,
      infoAnswer: TXTWCL_Unfortunately_we_canXt_extend__ELL
    },
    {
      infoQuestion: TXTWCL_What_if_IXve_signed_up_to_tour_ELL,
      infoAnswer: TXTWCL_DonXt_worryX_weXll_automatical_ELL
    },
    {
      infoQuestion: TXTWCL_How_can_I_manage_my_spendingX,
      infoAnswer: TXTWCL_If_youXre_not_sure_about_takin_ELL
    }
  ],
  default: [
    {
      infoQuestion: TXTWCL_Can_this_be_undoneX,
      infoAnswer: TXTWCL_Unfortunately_notX_Once_youXve_ELL
    },
    {
      infoQuestion: TXTWCL_Can_I_still_access_my_accountX,
      infoAnswer: TXTWCL_Yes_you_canX_But_you_wonXt_be__ELL
    },
    {
      infoQuestion: TXTWCL_What_about_bonusesX,
      infoAnswer: TXTWCL_Unfortunately_we_canXt_extend__ELL
    },
    {
      infoQuestion: TXTWCL_What_if_IXve_signed_up_to_tour_ELL,
      infoAnswer: TXTWCL_DonXt_worryX_weXll_automatical_ELL
    },
    {
      infoQuestion: TXTWCL_How_can_I_manage_my_spendingX,
      infoAnswer: TXTWCL_If_youXre_not_sure_about_takin_ELL
    }
  ]
};

const TakeABreakFromUsInformation: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const license = useSelector(select.account.getLicense);
  const showVegasContent = useSelector(select.site.showVegasContent);
  const depositLimitPath = useSelector(select.account.getDepositLimitPath);
  const isEmbedded = useSelector(select.site.isEmbedded);
  const { modal } = useDispatch<Dispatch>();
  const openRestrictDepositLimit = () => {
    isEmbedded
      ? call(['app', 'execute'], '://account/responsible/deposit-limit')
      : modal.replace([{ path: depositLimitPath }]);
  };
  const openAccumulatedLossLimit = () => {
    isEmbedded
      ? call(['app', 'execute'], '://account/responsible/accumulated-loss-limit')
      : modal.replace([{ path: '/account/responsible/accumulated-loss-limit' }]);
  };
  const visibilityPermanentSelfExclusionUK = useFeature(
    FeatureFlag.VisibilityPermanentSelfExclusionUK
  );
  const visibilityOASISExclusionOption = useFeature(FeatureFlag.VisibilityOASISExclusionOption);
  const EUDESelfExcludeRegulatorAlignment = useFeature(
    FeatureFlag.EUDESelfExcludeRegulatorAlignment
  );

  const infoMessagesDisplay = visibilityOASISExclusionOption
    ? infoMessages.selfExclusionDE
    : visibilityPermanentSelfExclusionUK
    ? infoMessages.selfExclusionUK
    : infoMessages[license] ?? infoMessages.default;

  const isCOMEULicense =
    [License.COM, License.EU, License.NL].includes(license) && !showVegasContent;

  return (
    <div className={styles.formContainer}>
      <div className={styles.infoTitle} onClick={() => setIsExpanded(!isExpanded)}>
        <div className={sharedStyles.formHeader}>
          <Translation {...TXTWCL_What_does_this_meanX} />
        </div>
        <div className={styles.expandIcon}>{!isExpanded ? <ExpandIcon /> : <MinIcon />}</div>
      </div>
      {isExpanded && (
        <>
          {visibilityOASISExclusionOption ? (
            <Translation tagName="p" {...TXTWCL_Known_as_XbXself_exclusionX_bX_ELL} />
          ) : (
            <>
              <Translation
                tagName="p"
                {...(license === License.CAON
                  ? TXTWCL_ItXs_basically_a_pause_on_your_ELL_1
                  : TXTWCL_ItXs_basically_a_pause_on_your_ELL)}
              />
              <div className={sharedStyles.infoMessage}>
                <Translation
                  tagName="b"
                  {...(license === License.EE
                    ? TXTWCL_How_long_can_I_be_excluded_for_ELL
                    : TXTWCL_How_long_can_I_take_a_break_fo_ELL)}
                />
                <div>
                  {license === License.UK ? (
                    <>
                      <Translation {...TXTWCL_You_choose_the_start_and_end_d_ELL_1} />{' '}
                      <Translation {...TXTWCL_This_can_be_anywhere_from_1_da_ELL} />
                    </>
                  ) : (
                    <>
                      <Translation {...TXTWCL_You_choose_the_start_and_end_d_ELL_1} />
                      {EUDESelfExcludeRegulatorAlignment ? (
                        <Translation
                          {...TXTWCL_This_can_be_anywhere_from_N0_d_ELL_1}
                          values={{ 0: 90 }}
                        />
                      ) : (
                        <Translation
                          {...(licenseSpecificText[license] ?? licenseSpecificText.default)}
                          values={{
                            0: (licenseSpecificPeriod[license] || licenseSpecificPeriod.default)[0],
                            1: (licenseSpecificPeriod[license] || licenseSpecificPeriod.default)[1]
                          }}
                        />
                      )}
                    </>
                  )}
                  {isCOMEULicense && (
                    <>
                      {' '}
                      <Translation {...TXTWCL_Alternatively_you_can_choose_t_ELL} />
                    </>
                  )}
                </div>
              </div>
            </>
          )}
          {infoMessagesDisplay.map(
            ({ infoQuestion, infoAnswer }: QuestionAnswerTagsPair, index: number) => (
              <div className={sharedStyles.infoMessage} key={index}>
                <Translation tagName="b" {...infoQuestion} />
                {isArray(infoAnswer) ? (
                  <div>
                    {infoAnswer.map((answerSentence, sentenceIndex) => (
                      <>
                        <Translation
                          tagName="span"
                          {...answerSentence}
                          key={sentenceIndex}
                          values={{ 0: 180 }}
                        />{' '}
                      </>
                    ))}
                  </div>
                ) : (
                  <Translation tagName="div" {...infoAnswer} />
                )}
              </div>
            )
          )}
          {!visibilityPermanentSelfExclusionUK && !isCOMEULicense && (
            <div className={sharedStyles.infoMessage}>
              <Translation tagName="b" {...TXTWCL_Can_you_exclude_me_permanently_ELL} />
              {license === License.EE ? (
                <div>
                  <Translation {...TXTWCL_Of_courseX_1} />{' '}
                  <Translation
                    {...TXTWCL_Just_contact_us_through_the_Xa_ELL}
                    values={{
                      a: (msg: string) => (
                        <ResolverLink resolverTag={ResolverTag.ContactSupport}>{msg}</ResolverLink>
                      )
                    }}
                  />
                </div>
              ) : (
                <Translation
                  tagName="div"
                  {...TXTWCL_Of_courseX_Just_XaXcontact_usX_ELL}
                  values={{
                    a: (msg: string) => (
                      <ResolverLink
                        resolverTag={
                          license === License.CAON
                            ? ResolverTag.RGQuestion
                            : ResolverTag.ContactSupport
                        }
                      >
                        {msg}
                      </ResolverLink>
                    )
                  }}
                />
              )}
            </div>
          )}
          {license === License.UK && (
            <div className={sharedStyles.infoMessage}>
              <Translation tagName="b" {...TXTWCL_Can_I_take_a_break_from_all_ga_ELL} />
              <div>
                <Translation {...TXTCLI_YesX} />{' '}
                <Translation
                  {...TXTWCL_Simply_visit_XaXwwwXgamstopXco_ELL}
                  values={{
                    a: (msg: string) => (
                      <ResolverLink resolverTag={ResolverTag.SelfExclusionGamStop}>
                        {msg}
                      </ResolverLink>
                    )
                  }}
                />
              </div>
            </div>
          )}
          {license === License.DESH && (
            <div className={sharedStyles.infoMessage}>
              <Translation tagName="b" {...TXTWCL_Where_can_I_ask_for_advice_on__ELL} />
              <FormContentDESH informationTag={TXTWCL_In_case_you_are_interestedX_we_ELL} />
            </div>
          )}
          {visibilityOASISExclusionOption ? (
            <>
              {' '}
              <Translation tagName="b" {...TXTWCL_How_can_I_manage_my_spendingX} />
              <p>
                <Translation
                  {...TXTWCL_If_youXre_not_sure_about_takin_ELL_2}
                  values={{
                    a: (msg: string) => (
                      <StarsLink onClick={openRestrictDepositLimit}>{msg}</StarsLink>
                    )
                  }}
                />{' '}
                <Translation
                  {...TXTWCL_You_can_also_set_a_XaXloss_lim_ELL}
                  values={{
                    a: (msg: string) => (
                      <StarsLink onClick={openAccumulatedLossLimit}>{msg}</StarsLink>
                    )
                  }}
                />
              </p>
              <Translation
                tagName="p"
                {...TXTWCL_To_find_out_more_about_safe_ga_ELL}
                values={{
                  a: (msg: string) => (
                    <ResolverLink resolverTag={ResolverTag.RGPage}>{msg}</ResolverLink>
                  )
                }}
              />{' '}
            </>
          ) : visibilityPermanentSelfExclusionUK ? (
            <Translation
              {...TXTWCL_Learn_more_about_XaXResponsibl_ELL}
              values={{
                a: (msg: string) => (
                  <ResolverLink resolverTag={ResolverTag.DFAccountResponsibleGaming}>
                    {msg}
                  </ResolverLink>
                )
              }}
            />
          ) : (
            <Translation
              tagName="p"
              {...TXTWCL_To_find_out_more_about_safe_ga_ELL}
              values={{
                a: (msg: string) => (
                  <ResolverLink resolverTag={ResolverTag.SelfExclusionResponsibleGamingPS}>
                    {msg}
                  </ResolverLink>
                )
              }}
            />
          )}
        </>
      )}
    </div>
  );
};

export default TakeABreakFromUsInformation;
